import { StringContext } from "../../core/StringContext";
import { StringModule } from "../../core/StringModule";
import { CalculatedValue } from "../../models/text/CalculatedValue";
import { ISplitOptionItem } from "../../models/text/ISplitOptionItem";
import { ISplitOptions } from "../../models/text/ISplitOptions";
import { StringObject } from "../../objects/StringObject";
import { BuildDOMTree } from "../../utils/text/BuildDOMTree";
import { BuildTokens } from "../../utils/text/BuildTokens";
import { FontMetrics } from "../../utils/text/CanvasKerningApplier";
import { LayoutMeasurer } from "../../utils/text/LayoutMeasurer";
import {
  LayoutLine,
  ProcessedChar,
  ProcessedWord,
  SplitMeasuredTokens,
} from "../../utils/text/SplitMeasuredTokens";

type FitContext = {
  currentFontSize: number;
  contentWidth: number;
  /** Computed line-height in px at currentFontSize. 0 when unavailable (e.g. "normal"). */
  lineHeightPx: number;
};

/**
 * StringSplit module: splits text into lines, words, chars,
 * computes alignment/random values, and applies CSS vars.
 */
export class StringSplit extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "split";
    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    //this.permissions.mobile.rebuild.scrollHeight = false;
  }

  onInit(): void {
    const rebuild = () => {
      this.objectsOnPage.forEach((object) => {
        this.onObjectConnected(object);
      });
    };

    document.fonts.ready.then(rebuild);
    document.fonts.addEventListener("loadingdone", rebuild);
  }

  onResizeWidth(): void {
    this.objectsOnPage.forEach((object) => {
      this.onObjectConnected(object);
    });
  }

  onObjectConnected(object: StringObject): void {
    const element = object.htmlElement;
    if (!element) return;

    const isAlreadySplit = element.classList.contains("-splitted");
    let originalHtml = element.getAttribute("string-split-original");

    if (!isAlreadySplit || originalHtml === null) {
      originalHtml = this.escapeAttribute(element.innerHTML);
      element.setAttribute("string-split-original", originalHtml);
      element.classList.add("-splitted");
    }

    object.htmlElement.innerHTML = originalHtml;
    const attr =
      element.getAttribute("string-split") ?? element.getAttribute("data-string-split") ?? "";
    const options: ISplitOptions = this.tools.optionsParser.process({
      attributeValue: attr,
    });
    const { fragment, result, extraProps } = this.split(element, options);
    object.setProperty("nodes", fragment.childNodes);
    element.setAttribute("aria-label", originalHtml);
    element.innerHTML = "";
    element.appendChild(result);

    extraProps.forEach((value: string, key: string) => {
      element.style.setProperty(key, value);
    });

    const restoreAfter = element.getAttribute("string-split-restore-after");
    if (restoreAfter && !isNaN(Number(restoreAfter))) {
      setTimeout(() => {
        element.innerHTML = originalHtml!;
        element.classList.add("-restored");
      }, Number(restoreAfter));
    }
  }

  public split(element: HTMLElement, options: ISplitOptions) {
    const fontMetrics = new FontMetrics(element);
    const frag = document.createDocumentFragment();
    element.childNodes.forEach((ch) => frag.appendChild(ch.cloneNode(true)));
    const tokens = BuildTokens(frag.childNodes as NodeListOf<ChildNode>);
    const measured = LayoutMeasurer(tokens, element, fontMetrics);
    const layoutLines: LayoutLine[] = SplitMeasuredTokens(measured, element, fontMetrics);
    const fitContext = options.fit ? this.getFitContext(layoutLines, element) : null;
    const fitExtraProps = fitContext
      ? this.applyFit(layoutLines, options, fitContext)
      : new Map<string, string>();

    this.applyCalculatedValues(layoutLines, options);

    const dom = BuildDOMTree(layoutLines, options, fontMetrics);
    fitExtraProps.forEach((v, k) => dom.extraProps.set(k, v));

    if (fitContext) {
      this.refineFitFontSize(
        element,
        dom.fragment,
        dom.extraProps,
        layoutLines,
        options,
        fitContext,
      );
    }

    return {
      fragment: frag,
      result: dom.fragment,
      extraProps: dom.extraProps,
    };
  }

  private getFitContext(layoutLines: LayoutLine[], element: HTMLElement): FitContext | null {
    const cs = window.getComputedStyle(element);
    const currentFontSize = parseFloat(cs.fontSize);
    if (!currentFontSize) return null;

    const elementContentWidth =
      element.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
    if (elementContentWidth <= 0) return null;

    let widestLineWidth = 0;
    for (const line of layoutLines) {
      const w = line.fitWidth ?? line.rect.width;
      if (w > widestLineWidth) widestLineWidth = w;
    }
    if (widestLineWidth <= 0) return null;

    let contentWidth = elementContentWidth;
    if (Math.abs(elementContentWidth - widestLineWidth) < 2 && element.parentElement) {
      const pcs = window.getComputedStyle(element.parentElement);
      const parentContentWidth =
        element.parentElement.clientWidth -
        (parseFloat(pcs.paddingLeft) || 0) -
        (parseFloat(pcs.paddingRight) || 0);
      if (parentContentWidth > elementContentWidth) {
        contentWidth = parentContentWidth;
      }
    }

    const lineHeightPx = parseFloat(cs.lineHeight) || 0;
    return { currentFontSize, contentWidth, lineHeightPx };
  }

  /**
   * Computes target font sizes to fill the container width and stores them on each line.
   *
   * With a line-level split (`line`, `wordLine`, `charLine`): sets `fitFontSize` on each
   * `LayoutLine` so that BuildDOMTree can expose `--fit-font-size` per `-s-line` span.
   * Each line may receive a different value, allowing text to fill 100% of the container
   * width independently per line.
   *
   * Without a line-level split: finds the widest line and returns a single
   * `--fit-font-size` in the extraProps map, to be applied to the container element.
   *
   * Formula: targetFontSize = currentFontSize * (contentWidth / lineWidth)
   */
  private applyFit(
    layoutLines: LayoutLine[],
    options: ISplitOptions,
    fitContext: FitContext,
  ): Map<string, string> {
    const extraProps = new Map<string, string>();
    const { currentFontSize, contentWidth } = fitContext;

    const hasLineSplit =
      (options.line?.length ?? 0) > 0 ||
      (options.wordLine?.length ?? 0) > 0 ||
      (options.charLine?.length ?? 0) > 0;

    // Space-aware formula only when individual chars are split (spaces become unscaled text nodes).
    // For line/word-only splits the entire span scales uniformly, so spaces scale too.
    const hasCharSplit =
      (options.char?.length ?? 0) > 0 ||
      (options.charLine?.length ?? 0) > 0 ||
      (options.charWord?.length ?? 0) > 0;

    if (hasLineSplit) {
      for (const line of layoutLines) {
        const lineWidth = line.fitWidth ?? line.rect.width;
        if (lineWidth > 0) {
          line.fitFontSize = this.computeFitFontSize(
            currentFontSize,
            contentWidth,
            lineWidth,
            hasCharSplit ? line.browserWordWidthSum : undefined,
          );
        }
      }
    } else {
      const widestLine = layoutLines.reduce((best, l) => {
        const w = l.fitWidth ?? l.rect.width;
        return w > (best.fitWidth ?? best.rect.width) ? l : best;
      }, layoutLines[0]);
      const widestLineWidth = widestLine.fitWidth ?? widestLine.rect.width;
      const targetFontSize = this.computeFitFontSize(
        currentFontSize,
        contentWidth,
        widestLineWidth,
        hasCharSplit ? widestLine.browserWordWidthSum : undefined,
      );
      extraProps.set("--fit-font-size", String(Math.floor(targetFontSize)));
    }

    return extraProps;
  }

  private refineFitFontSize(
    element: HTMLElement,
    fragment: DocumentFragment,
    extraProps: Map<string, string>,
    layoutLines: LayoutLine[],
    options: ISplitOptions,
    fitContext: FitContext,
  ): void {
    const hasLineSplit =
      (options.line?.length ?? 0) > 0 ||
      (options.wordLine?.length ?? 0) > 0 ||
      (options.charLine?.length ?? 0) > 0;
    const hasCharSplit =
      (options.char?.length ?? 0) > 0 ||
      (options.charLine?.length ?? 0) > 0 ||
      (options.charWord?.length ?? 0) > 0;

    const originalHtml = element.innerHTML;

    try {
      element.innerHTML = "";
      element.appendChild(fragment.cloneNode(true));

      if (hasLineSplit) {
        const renderedLines = Array.from(element.querySelectorAll(".-s-line")) as HTMLElement[];
        const resultLines = Array.from(fragment.querySelectorAll(".-s-line")) as HTMLElement[];

        renderedLines.forEach((renderedLine, index) => {
          const resultLine = resultLines[index];
          if (!resultLine) return;

          const provisional = parseFloat(resultLine.style.getPropertyValue("--fit-font-size"));
          if (!provisional) return;

          const corrected = this.solveRenderedFitFontSize(
            renderedLine,
            fitContext.currentFontSize,
            provisional,
            fitContext.contentWidth,
            hasCharSplit,
          );
          if (!corrected) return;

          layoutLines[index].fitFontSize = corrected;
          resultLine.style.setProperty("--fit-font-size", String(Math.floor(corrected)));
        });
      } else {
        const provisional = parseFloat(extraProps.get("--fit-font-size") ?? "");
        if (!provisional) return;

        const corrected = this.solveRenderedFitFontSize(
          element,
          fitContext.currentFontSize,
          provisional,
          fitContext.contentWidth,
          hasCharSplit,
        );
        if (!corrected) return;

        const finalFontSize = Math.floor(corrected);
        extraProps.set("--fit-font-size", String(finalFontSize));

        const { lineHeightPx, currentFontSize, contentWidth } = fitContext;
        if (lineHeightPx > 0 && currentFontSize > 0) {
          const textHeightAtFit = lineHeightPx * (finalFontSize / currentFontSize);
          if (textHeightAtFit > 0) {
            extraProps.set("--fit-scale-y", String(window.innerHeight / textHeightAtFit));
            extraProps.set("--fit-aspect-ratio", String(contentWidth / textHeightAtFit));
          }
        }
      }
    } finally {
      element.innerHTML = originalHtml;
    }
  }

  private solveRenderedFitFontSize(
    scope: HTMLElement,
    currentFontSize: number,
    provisionalFontSize: number,
    targetWidth: number,
    applyToChars: boolean,
  ): number | null {
    if (!Number.isFinite(currentFontSize) || !Number.isFinite(provisionalFontSize)) return null;
    if (currentFontSize <= 0 || provisionalFontSize <= 0) return null;

    const baselineWidth = this.measureScopeAtFontSize(scope, applyToChars, currentFontSize);
    if (baselineWidth <= 0) return null;
    if (Math.abs(targetWidth - baselineWidth) < 0.01) {
      return currentFontSize;
    }

    const trialWidth =
      Math.abs(provisionalFontSize - currentFontSize) < 0.01
        ? baselineWidth
        : this.measureScopeAtFontSize(scope, applyToChars, provisionalFontSize);

    if (trialWidth <= 0) {
      return currentFontSize * (targetWidth / baselineWidth);
    }

    const slope = (trialWidth - baselineWidth) / (provisionalFontSize - currentFontSize);
    if (!Number.isFinite(slope) || Math.abs(slope) < 0.0001) {
      return currentFontSize * (targetWidth / baselineWidth);
    }

    const corrected = currentFontSize + (targetWidth - baselineWidth) / slope;
    if (!Number.isFinite(corrected) || corrected <= 0) return null;
    return corrected;
  }

  private measureScopeAtFontSize(
    scope: HTMLElement,
    applyToChars: boolean,
    fontSize: number,
  ): number {
    if (applyToChars) {
      const chars = Array.from(scope.querySelectorAll(".-s-char")) as HTMLElement[];
      const restore = chars.map((char) => char.style.fontSize);
      chars.forEach((char) => {
        char.style.fontSize = `${fontSize}px`;
      });

      scope.offsetWidth;
      const width = this.measureCharScopeWidth(scope);

      chars.forEach((char, index) => {
        char.style.fontSize = restore[index];
      });
      return width;
    }

    const previous = scope.style.fontSize;
    scope.style.fontSize = `${fontSize}px`;
    scope.offsetWidth;
    const width = this.measureContentWidth(scope);
    scope.style.fontSize = previous;
    return width;
  }

  private measureCharScopeWidth(scope: HTMLElement): number {
    let width = 0;

    const chars = Array.from(scope.querySelectorAll(".-s-char")) as HTMLElement[];
    chars.forEach((char) => {
      const rect = char.getBoundingClientRect();
      const style = window.getComputedStyle(char);
      width +=
        rect.width + (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
    });

    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT);
    let textNode: Node | null = walker.nextNode();
    while (textNode) {
      const parent = textNode.parentElement;
      const inChar = !!parent?.closest(".-s-char");
      const inLeafElement =
        !!parent &&
        !parent.classList.contains("-s-char") &&
        !parent.classList.contains("-s-word") &&
        !parent.classList.contains("-s-line") &&
        !parent.querySelector(".-s-char, .-s-word, .-s-line");

      if (!inChar && !inLeafElement && textNode.textContent?.length) {
        const range = document.createRange();
        range.selectNodeContents(textNode);
        width += range.getBoundingClientRect().width;
      }

      textNode = walker.nextNode();
    }

    const leafElements = Array.from(scope.querySelectorAll("*")).filter((node) => {
      const el = node as HTMLElement;
      return (
        !el.classList.contains("-s-char") &&
        !el.classList.contains("-s-word") &&
        !el.classList.contains("-s-line") &&
        !el.querySelector(".-s-char, .-s-word, .-s-line")
      );
    }) as HTMLElement[];

    leafElements.forEach((leaf) => {
      width += leaf.getBoundingClientRect().width;
    });

    return width;
  }

  private measureContentWidth(scope: HTMLElement): number {
    if (!scope.childNodes.length) {
      return scope.getBoundingClientRect().width;
    }

    const range = document.createRange();
    range.selectNodeContents(scope);
    return range.getBoundingClientRect().width;
  }

  /**
   * Computes the target font size to fill `contentWidth`.
   *
   * Inter-word `\u00a0` nodes in the DOM are NOT wrapped in `-s-char` spans, so they
   * stay at the original font size and do not scale. The formula accounts for this by
   * subtracting total space width (fitWidth - browserWordWidthSum) from both sides:
   *
   *   targetFontSize = currentFontSize * (contentWidth - spacesWidth) / (lineWidth - spacesWidth)
   *
   * For single-word lines spacesWidth = 0 and the formula reduces to the simple ratio.
   */
  private computeFitFontSize(
    currentFontSize: number,
    contentWidth: number,
    lineWidth: number,
    browserWordWidthSum: number | undefined,
  ): number {
    const spacesWidth = browserWordWidthSum !== undefined ? lineWidth - browserWordWidthSum : 0;
    const scalableWidth = lineWidth - spacesWidth;
    if (scalableWidth <= 0) return currentFontSize * (contentWidth / lineWidth);
    return (currentFontSize * (contentWidth - spacesWidth)) / scalableWidth;
  }

  /**
   * Computes a numeric value based on the provided split option, index, and total count.
   *
   * @param opt - The split option item containing alignment and optional random range.
   * @param index - The current index in the sequence.
   * @param total - The total number of items in the sequence.
   * @returns A computed numeric value based on the alignment strategy:
   *          - "random": A random value within the specified range or default range.
   *          - "start": The current index.
   *          - "end": The reverse index from the end of the sequence.
   *          - "center": The distance from the center index.
   *          - Default: The current index.
   */
  private computeValue(opt: ISplitOptionItem, index: number, total: number): number {
    if (opt.align.startsWith("random")) {
      const min = opt.random?.min ?? 0;
      const max = opt.random?.max ?? total - 1;
      return Math.floor(Math.random() * (max - min + 1)) + min;
    }
    switch (opt.align) {
      case "start":
        return index;
      case "end":
        return total - index - 1;
      case "center": {
        const centerIdx = Math.floor((total - 1) / 2);
        return Math.abs(index - centerIdx);
      }
      default:
        return index;
    }
  }

  /**
   * Applies calculated values to the provided layout lines and their components (words and characters)
   * based on the given options. This method computes and assigns various calculated values
   * (e.g., alignment and value) to lines, words, and characters, depending on the specified options.
   *
   * @param lines - An array of `LayoutLine` objects representing the lines of text to process.
   * @param options - An `ISplitOptions` object specifying the calculation options for lines, words, and characters.
   *
   * The `options` parameter can include the following properties:
   * - `line`: An array of options for calculating values at the line level.
   * - `word`: An array of options for calculating values at the word level.
   * - `wordLine`: An array of options for calculating values at the word level relative to the line.
   * - `char`: An array of options for calculating values at the character level.
   * - `charWord`: An array of options for calculating values at the character level relative to the word.
   * - `charLine`: An array of options for calculating values at the character level relative to the line.
   *
   * Each calculated value is assigned to the corresponding `calculatedValues` property of the line, word, or character.
   * The method ensures that all specified options are processed and the calculated values are appended accordingly.
   */
  private applyCalculatedValues(lines: LayoutLine[], options: ISplitOptions): void {
    const countChars = (ln: LayoutLine) =>
      ln.words.reduce((acc, w: ProcessedWord) => acc + w.chars.length, 0);

    const totalWords = lines.reduce((acc, l) => acc + l.words.length, 0);
    const totalCharsGlobal = lines.reduce(
      (acc, l) => acc + l.words.reduce((acc2, w) => acc2 + w.chars.length, 0),
      0,
    );

    lines.forEach((line, li) => {
      if (options.line) {
        (line as any).calculatedValues = options.line.map(
          (opt) =>
            ({
              type: "line",
              align: opt.align,
              value: this.computeValue(opt, li, lines.length),
            }) as CalculatedValue,
        );
      }

      line.words.forEach((word: ProcessedWord) => {
        if (options.word) {
          (word as any).calculatedValues = options.word.map(
            (opt) =>
              ({
                type: "word",
                align: opt.align,
                value: this.computeValue(opt, word.wordIndexGlobal, totalWords),
              }) as CalculatedValue,
          );
        }

        if (options.wordLine) {
          (word as any).calculatedValues ??= [];
          (word as any).calculatedValues.push(
            ...options.wordLine.map(
              (opt) =>
                ({
                  type: "wordLine",
                  align: opt.align,
                  value: this.computeValue(opt, word.wordIndexInLine, line.words.length),
                }) as CalculatedValue,
            ),
          );
        }

        const totalCharsInLine = countChars(line);

        word.chars.forEach((char: ProcessedChar) => {
          const cvs: CalculatedValue[] = [];

          if (options.char) {
            cvs.push(
              ...options.char.map(
                (opt) =>
                  ({
                    type: "char",
                    align: opt.align,
                    value: this.computeValue(opt, char.charIndexGlobal, totalCharsGlobal),
                  }) as CalculatedValue,
              ),
            );
          }

          if (options.charWord) {
            cvs.push(
              ...options.charWord.map(
                (opt) =>
                  ({
                    type: "charWord",
                    align: opt.align,
                    value: this.computeValue(opt, char.charIndexInWord, word.chars.length),
                  }) as CalculatedValue,
              ),
            );
          }

          if (options.charLine) {
            cvs.push(
              ...options.charLine.map(
                (opt) =>
                  ({
                    type: "charLine",
                    align: opt.align,
                    value: this.computeValue(opt, char.charIndexInLine, totalCharsInLine),
                  }) as CalculatedValue,
              ),
            );
          }

          (char as any).calculatedValues = cvs;
        });
      });
    });
  }

  /**
   * Escapes the `src` attribute in a given string by removing the quotes around the URL.
   *
   * This method searches for occurrences of `src="URL"` where `URL` is an HTTP or HTTPS link,
   * and replaces it with `src=URL` (removing the quotes around the URL).
   *
   * @param str - The input string containing the `src` attributes to be escaped.
   * @returns The modified string with escaped `src` attributes.
   */
  private escapeAttribute(str: string): string {
    return str.replace(/src="(https?:\/\/[^"\s]+)"/g, "src=$1");
  }
}
